import * as React from 'react';

import type { PatientOutcomeSurface } from '@heidi/patients/contracts';
import type { PatientProfileAppointmentCandidates } from '@heidi/session/contracts';

import { useSessionListLinkController } from '../../hooks/useSessionListLinkController';
import { PatientSearchCombobox } from '../patient-search-combobox';

interface SessionListLinkPopoverContentProps {
  /** Sessions to link the selected (or newly-created) patient to. */
  selectedSessionIds: string[];
  /** Closes the parent popover/drawer after a successful action. */
  setOpen: (open: boolean) => void;
  /**
   * Called when the user clicks Create new patient with no typed text.
   * The parent (`SessionListLinkPopover`) opens the legacy full-metadata
   * create modal — mounted there so it survives the popover closing.
   */
  onRequestEmptyCreate: () => void;
  /** Placeholder for the search input. */
  commandInputPlaceholder?: string;
  /** Disables interaction (e.g. read-only sessions). */
  disabled?: boolean;
  /** Candidate payload returned on the initiating session. */
  patientProfileAppointmentCandidates?: PatientProfileAppointmentCandidates | null;
  /**
   * `patient_linked` surface attribution for this entry point (row avatar vs
   * bulk bar). Omitted by legacy callers — the event then fires bare.
   */
  trackingSurface?: PatientOutcomeSurface;
}

/**
 * Find-or-link content for the session-list link popover. A thin adapter:
 * `useSessionListLinkController` produces the props and the SAME
 * `PatientSearchCombobox` the in-session header uses renders them — in
 * `variant="popover"` mode. Keeping one component across both surfaces means
 * the search list, suggested matches, and the Create / Create-unlinked-session
 * footer can never drift between the header and the list.
 */
export const SessionListLinkPopoverContent: React.FC<
  SessionListLinkPopoverContentProps
> = ({
  selectedSessionIds,
  setOpen,
  onRequestEmptyCreate,
  commandInputPlaceholder,
  disabled = false,
  patientProfileAppointmentCandidates,
  trackingSurface,
}) => {
  const onClose = React.useCallback(() => setOpen(false), [setOpen]);
  const controller = useSessionListLinkController({
    selectedSessionIds,
    trackingSurface,
    patientProfileAppointmentCandidates,
    onRequestEmptyCreate,
    onClose,
    disabled,
  });

  return (
    <PatientSearchCombobox
      variant="popover"
      placeholder={commandInputPlaceholder}
      data-testid="session-list-link-combobox"
      {...controller}
    />
  );
};
